import { AbpMultiTenancyService } from '@abp/multi-tenancy/abp-multi-tenancy.service';
import { AbpSessionService } from '@abp/session/abp-session.service';
import { Component, Injector, OnInit, ViewChild, ViewEncapsulation, Inject, Optional } from '@angular/core';
import { ImpersonationService } from '@app/admin/users/impersonation.service';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { LinkedAccountService } from '@app/shared/layout/linked-account.service';
import { UserNotificationHelper } from '@app/shared/layout/notifications/UserNotificationHelper';
import { NotificationSettingsModalComponent } from '@app/shared/layout/notifications/notification-settings-modal.component';
import { AppConsts } from '@shared/AppConsts';
import { EditionPaymentType, SubscriptionStartType } from '@shared/AppEnums';
import { AppComponentBase } from '@shared/common/app-component-base';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { ChangeUserLanguageDto, GetCurrentLoginInformationsOutput, LinkedUserDto, ProfileServiceProxy, SessionServiceProxy, TenantLoginInfoDto, UserLinkServiceProxy, UserServiceProxy, API_BASE_URL } from '@shared/service-proxies/service-proxies';
import * as _ from 'lodash';
import * as moment from 'moment';
import { LinkedAccountsModalComponent } from './linked-accounts-modal.component';
import { LoginAttemptsModalComponent } from './login-attempts-modal.component';
import { ChangePasswordModalComponent } from './profile/change-password-modal.component';
import { ChangeProfilePictureModalComponent } from './profile/change-profile-picture-modal.component';
import { MySettingsModalComponent } from './profile/my-settings-modal.component';
import { LocalStorageHelper } from '@app/shared/common/helpers/LocalStorageHelper';
import { ImportNotificationService } from '@shared/common/import.notification';
import { MsalService } from "@azure/msal-angular";
import { environment } from 'environments/environment';
import { AppPreBootstrap } from 'AppPreBootstrap';
import { AuthService } from '../common/auth/auth.service';

@Component({
    templateUrl: './header.component.html',
    selector: 'header-bar',
    encapsulation: ViewEncapsulation.None
})
export class HeaderComponent extends AppComponentBase implements OnInit {

    @ViewChild('notificationSettingsModal') notificationSettingsModal: NotificationSettingsModalComponent;

    @ViewChild('loginAttemptsModal') loginAttemptsModal: LoginAttemptsModalComponent;
    @ViewChild('linkedAccountsModal') linkedAccountsModal: LinkedAccountsModalComponent;
    @ViewChild('changePasswordModal') changePasswordModal: ChangePasswordModalComponent;
    @ViewChild('changeProfilePictureModal') changeProfilePictureModal: ChangeProfilePictureModalComponent;
    @ViewChild('mySettingsModal') mySettingsModal: MySettingsModalComponent;

    languages: abp.localization.ILanguageInfo[];
    currentLanguage: abp.localization.ILanguageInfo;
    isImpersonatedLogin = false;
    isMultiTenancyEnabled = false;

    shownLoginNameTitle = '';
    shownLoginName = '';

    tenancyName = '';
    userName = '';

    profilePicture = AppConsts.appBaseUrl + '/assets/common/images/default-profile-picture.png';
    defaultLogo = AppConsts.appBaseUrl + '/assets/common/images/app-logo-on-' + this.ui.getAsideSkin() + '.png';
    recentlyLinkedUsers: LinkedUserDto[];
    unreadChatMessageCount = 0;

    remoteServiceBaseUrl: string = AppConsts.remoteServiceBaseUrl;

    chatConnected = false;

    tenant: TenantLoginInfoDto = new TenantLoginInfoDto();
    subscriptionStartType = SubscriptionStartType;
    editionPaymentType: typeof EditionPaymentType = EditionPaymentType;

    unRead: number = 0;
    userMessages: UserMessage;
    baseUrl: string = '';


    constructor(
        injector: Injector,
        private _abpSessionService: AbpSessionService,
        private _abpMultiTenancyService: AbpMultiTenancyService,
        private _profileServiceProxy: ProfileServiceProxy,
        private _userLinkServiceProxy: UserLinkServiceProxy,
        private _userServiceProxy: UserServiceProxy,
        private _authService: AppAuthService,
        private _impersonationService: ImpersonationService,
        private _linkedAccountService: LinkedAccountService,
        private _userNotificationHelper: UserNotificationHelper,
        private _sessionService: SessionServiceProxy,
        private _appSessionService: AppSessionService,
        private _importNotificationService: ImportNotificationService,
        private authService: MsalService,
        private authServiceNew: AuthService,
        @Optional() @Inject(API_BASE_URL) baseUrl?: string

    ) {
        super(injector);
        this.baseUrl = baseUrl ? baseUrl : "";
    }

    get multiTenancySideIsTenant(): boolean {
        return this._abpSessionService.tenantId > 0;
    }

    ngOnInit() {
        this.isMultiTenancyEnabled = this._abpMultiTenancyService.isEnabled;
        this._userNotificationHelper.settingsModal = this.notificationSettingsModal;

        this.languages = _.filter(this.localization.languages, l => (<any>l).isDisabled === false);
        this.currentLanguage = this.localization.currentLanguage;
        this.isImpersonatedLogin = this._abpSessionService.impersonatorUserId > 0;

        this.shownLoginNameTitle = this.isImpersonatedLogin ? this.l('YouCanBackToYourAccount') : '';
        this.getCurrentLoginInformations();
        this.getProfilePicture();
        this.getRecentlyLinkedUsers();

        this.registerToEvents();
        this.registerStorageMessage();
        this.customAbpWaringPopup();
    }

    registerToEvents() {
        abp.event.on('profilePictureChanged', () => {
            this.getProfilePicture();
        });

        abp.event.on('app.chat.unreadMessageCountChanged', messageCount => {
            this.unreadChatMessageCount = messageCount;
        });

        abp.event.on('app.chat.connected', () => {
            this.chatConnected = true;
        });
    }

    changeLanguage(languageName: string): void {
        const input = new ChangeUserLanguageDto();
        input.languageName = languageName;

        this._profileServiceProxy.changeLanguage(input).subscribe(() => {
            abp.utils.setCookieValue(
                'Abp.Localization.CultureName',
                languageName,
                new Date(new Date().getTime() + 5 * 365 * 86400000), //5 year
                abp.appPath
            );

            window.location.reload();
        });
    }

    getCurrentLoginInformations(): void {
        this.shownLoginName = this.appSession.getShownLoginName();
        this.tenancyName = this.appSession.tenancyName;
        this.userName = this.appSession.user.userName;

        this._sessionService.getCurrentLoginInformations()
            .subscribe((result: GetCurrentLoginInformationsOutput) => {
                this.tenant = result.tenant;
            });
    }

    getShownUserName(linkedUser: LinkedUserDto): string {
        if (!this._abpMultiTenancyService.isEnabled) {
            return linkedUser.username;
        }

        return (linkedUser.tenantId ? linkedUser.tenancyName : '.') + '\\' + linkedUser.username;
    }

    getProfilePicture(): void {
        this._profileServiceProxy.getProfilePicture().subscribe(result => {
            if (result && result.profilePicture) {
                this.profilePicture = 'data:image/jpeg;base64,' + result.profilePicture;
            }
        });
    }

    getRecentlyLinkedUsers(): void {
        this._userLinkServiceProxy.getRecentlyUsedLinkedUsers().subscribe(result => {
            this.recentlyLinkedUsers = result.items;
        });
    }

    showLoginAttempts(): void {
        this.loginAttemptsModal.show();
    }

    showLinkedAccounts(): void {
        this.linkedAccountsModal.show();
    }

    changePassword(): void {
        this.changePasswordModal.show();
    }

    changeProfilePicture(): void {
        this.changeProfilePictureModal.show();
    }

    changeMySettings(): void {
        this.mySettingsModal.show();
    }

    logout(): void {
        // //logout MS if exists
        // if (this.authService.instance.getAllAccounts().length > 0) {
        //     this.authService.logoutPopup({
        //         mainWindowRedirectUri: "/"
        //       }).subscribe(res => {
        //         this._authService.logout();
        //     });
        // } else {
        //     this._authService.logout();
        // }


        //logout MS if exists
        var checkLoginMS = this.authService.getAccount();
        if (checkLoginMS) {
            this._authService.logout(false);
            this.authService.logout();
        } else {
            this._authService.logout();
        }
        let newAccessToken=AppPreBootstrap.getTokenLocalstorage(
          AppConsts.authorization.newAccessToken
        );
         if (newAccessToken) {
          this.authServiceNew.logout(newAccessToken).subscribe();
        }
        this.authServiceNew.clearToken();
        // AppPreBootstrap.clearTokenCookies();
        const currentUrl = encodeURIComponent(environment.homepageUrl);
        const loginUrl = `${environment.authUrl}auth/login?returnUrl=${currentUrl}&action=logout`;
        location.href = loginUrl;
    }

    onMySettingsModalSaved(): void {
        this.shownLoginName = this.appSession.getShownLoginName();
    }

    backToMyAccount(): void {
        this._impersonationService.backToImpersonator();
    }

    switchToLinkedUser(linkedUser: LinkedUserDto): void {
        this._linkedAccountService.switchToAccount(linkedUser.id, linkedUser.tenantId);
    }

    get chatEnabled(): boolean {
        return (!this._abpSessionService.tenantId || this.feature.isEnabled('App.ChatFeature'));
    }

    subscriptionStatusBarVisible(): boolean {
        return this._appSessionService.tenantId > 0 && (this._appSessionService.tenant.isInTrialPeriod || this.subscriptionIsExpiringSoon());
    }

    subscriptionIsExpiringSoon(): boolean {
        if (this._appSessionService.tenant.subscriptionEndDateUtc) {
            return moment().utc().add(AppConsts.subscriptionExpireNootifyDayCount, 'days') >= moment(this._appSessionService.tenant.subscriptionEndDateUtc);
        }

        return false;
    }

    getSubscriptionExpiringDayCount(): number {
        if (!this._appSessionService.tenant.subscriptionEndDateUtc) {
            return 0;
        }

        return Math.round(moment.utc(this._appSessionService.tenant.subscriptionEndDateUtc).diff(moment().utc(), 'days', true));
    }

    getTrialSubscriptionNotification(): string {
        return this.l('TrialSubscriptionNotification',
            '<strong>' + this._appSessionService.tenant.edition.displayName + '</strong>',
            '<a href=\'/account/buy?editionId=' + this._appSessionService.tenant.edition.id + '&editionPaymentType=' + this.editionPaymentType.BuyNow + '\'>' + this.l('ClickHere') + '</a>');
    }

    getExpireNotification(localizationKey: string): string {
        return this.l(localizationKey, this.getSubscriptionExpiringDayCount());
    }

    resetMessage() {
        this.unRead = 0;
    }

    registerStorageMessage() {
        abp.event.on('abp.notifications.received', userNotification => {
            let data = userNotification.notification.data;

            this._importNotificationService.trigger(data);
            let m: MessageModel = new MessageModel();
            let havelink = false;
            if (data.fileName && data.fileName != '') {
                m.url = this.baseUrl + '/Temp/Downloads' + '/' + data.fileName;
                m.target = '_blank';
                havelink = true;

                if (data.properties.folder) {
                    m.url = this.baseUrl + '/' + data.properties.folder + '/' + data.fileName;
                }
            }
            else {
                m.url = "javascript:void(0);";
                m.target = '_self';
                havelink = false;
            }
            m.date = new Date();
            m.message = data.message;
            this.userMessages.messageList.unshift(m);
            this.unRead += 1;
            if (data.success) {
                this.notify.success(this.l(data.message));
            } else {
                if (havelink) {
                    toastr.options.onclick = function () {
                        var link = document.createElement("a");
                        link.href = m.url;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    };

                    if (userNotification.notification.severity === abp.notifications.severity.WARN) {
                        this.notify.warn(this.l(data.message), "", toastr.options);
                    } else {
                        this.notify.error(this.l(data.message), "", toastr.options);
                    }
                } else {
                    this.notify.error(this.l(data.message));
                }
            }
            if (this.userMessages.messageList.length > 10) {
                this.userMessages.messageList = this.userMessages.messageList.slice(0, 10);
            }

            this.saveMessages(this.userMessages);

        });
        this.userMessages = this.getMessages(this.appSession.userId);
    }

    private getMessages(userId: number): UserMessage {
        let userMessageList = LocalStorageHelper.tryGetFromLocalStorage('AkaPointMessageList', new Array<UserMessage>());
        let index = userMessageList.findIndex(s => s.userId == userId);
        if (index == -1) {
            let newuserMessage = new UserMessage();
            newuserMessage.userId = userId;
            newuserMessage.messageList = new Array<MessageModel>();
            return newuserMessage;
        }
        else {
            return userMessageList[index];
        }
    }

    private saveMessages(userMessages: UserMessage): void {
        let userMessageList = LocalStorageHelper.tryGetFromLocalStorage('AkaPointMessageList', new Array<UserMessage>());
        let index = userMessageList.findIndex(s => s.userId == userMessages.userId);
        if (index == -1) {
            userMessageList.push(userMessages);
        }
        else {
            userMessageList[index].messageList = userMessages.messageList;
        }
        LocalStorageHelper.trySaveToLocalStorage('AkaPointMessageList', userMessageList);
    }

    formatFromNow(d: Date): string {
        if (d) {
            return moment(d).fromNow();
        }
        else {
            return "";
        }
    }

    customAbpWaringPopup() {
        (abp as any).libs.sweetAlert.config.warn.buttons = {
            text: this.l('Ok')
        };
    }
}

class MessageModel {
    url: string;
    target: string;
    date: Date;
    message: string;
}

class UserMessage {
    userId: number;
    messageList: MessageModel[];
}
